import { For, Show, createMemo, type Component } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { CrossIcon } from '~ui/icons';
import { formatPercent } from '~/lib/format';
import { candles, holdings } from '~data';
import { CloseChart } from '~/features/close-chart';
import { fundSymbolFor } from '~/data/look-through';
import { closeHoldings, drillInto, selectedSymbol } from '~/stores/explore-store';

// What the fund holds, drawn rather than listed.
//
// A fund of funds is the case this exists for: HDIV's ten positions are Hamilton
// ETFs, and "what am I actually buying" is answered by ten price lines far
// faster than by ten weights. For a fund holding companies directly it is
// currently a list of "no price history" — the price scrape covers funds, not
// their underlying names — so those rows say so rather than being hidden, and
// they fill in on their own if company prices are ever scraped.
const HoldingsPanel: Component = () => {
    const symbol = () => selectedSymbol();
    const positions = createMemo(() => holdings(symbol()));
    const priced = createMemo(() => positions().filter((h) => h.symbol != null && candles(h.symbol).length > 0));

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection>
                    <Heading level={2} class='pl-2'>
                        {symbol()} holdings
                    </Heading>
                </ToolbarSection>
                <ToolbarSection>
                    <Text tone='muted' class='whitespace-nowrap pr-2'>
                        {priced().length} of {positions().length} priced
                    </Text>
                    <Button size='fill' icon={<CrossIcon />} title='Close' onClick={closeHoldings} />
                </ToolbarSection>
            </Toolbar>

            <div class='flex-1 min-h-0 overflow-y-auto scrollbar-hide'>
                <Show when={positions().length > 0} fallback={<Text tone='muted' class='block px-2 py-4 text-center'>This fund publishes no holdings</Text>}>
                    <For each={positions()}>
                        {(holding) => {
                            // Resolved once per row: the header below switches
                            // element on it, and the ticker falls back to it for
                            // the issuers that publish fund holdings by name.
                            const inner = fundSymbolFor(holding);
                            return (
                            <div class='border-b'>
                                {/* The header doubles as the way in, where the
                                    holding is itself a fund: the chart says what
                                    it did, and clicking says what it is. */}
                                <Dynamic
                                    component={inner == null ? 'div' : 'button'}
                                    title={inner == null ? undefined : `Open ${inner}`}
                                    onClick={inner == null ? undefined : () => drillInto(inner)}
                                    class={`flex h-row w-full items-center justify-between gap-2 border-b bg-muted px-2 text-left text-sm ${inner == null ? '' : 'cursor-default hover:bg-muted-foreground/20'}`}>
                                    <div class='flex min-w-0 items-baseline gap-2'>
                                        <Show when={holding.symbol ?? inner}>
                                            {(ticker) => (
                                                <Text weight='medium' class='shrink-0'>
                                                    {ticker()}
                                                </Text>
                                            )}
                                        </Show>
                                        <Text tone='muted' class='truncate'>
                                            {holding.name}
                                        </Text>
                                    </div>
                                    {/* Negative is the cash line on a levered fund — the borrowing. */}
                                    <Text tone={holding.weight < 0 ? 'red' : undefined} class='shrink-0'>
                                        {formatPercent(holding.weight, 1)}
                                    </Text>
                                </Dynamic>
                                {/* Shorter than the fund's own chart above it: this
                                    is a column of them, and the shape is the point
                                    rather than the detail. */}
                                <Show when={holding.symbol} fallback={<Text tone='muted' class='block px-2 py-3 text-center'>No price history</Text>}>
                                    <CloseChart symbol={holding.symbol!} height='h-20' />
                                </Show>
                            </div>
                            );
                        }}
                    </For>
                </Show>
            </div>
        </Panel>
    );
};

export default HoldingsPanel;
